/**
 * WarmupRoutineService — the per-student routine document, the session-record
 * warmup log, and the two reads the lesson mount needs (catalog, last lesson).
 *
 * `warmupRoutines/{studentId}` — one doc per student, doc id = student id:
 *   { studentId, cell: {duration, level, ageBand, style, voice}, items: [...],
 *     updatedAt, updatedBy }
 * `items` is the assembler's shape (warmupRoutineModel.assembleRoutine →
 * items), stored as-is: slot, exerciseId, purpose, tier, bpm, span, minSpan,
 * maxSpan, extraReps, fixed, startTonic, topTonic, reps, seconds. Flat maps
 * and scalars only — no nested arrays (Firestore rejects them; see
 * warmupShape.js for the catalog's own translation).
 *
 * WRITES ARE WHOLE-DOCUMENT ON PURPOSE. A routine is edited as a draft and
 * saved as one act (Anthony, 2026-09-03: "an option to save the updated routine
 * or discard"), so `saveWarmupRoutine` replaces the doc rather than merging —
 * `{ merge: true }` is not deep and would leave a removed slot's siblings
 * behind inside `items` anyway (CLAUDE.md).
 *
 * THE LOG is a `warmups` map on the session record `lessons/{occ_…}` — the doc
 * the timer and attendance already write — teacher-only by the lessons rule.
 * Written whole each time too: this hook owns the map, and a map on updateDoc
 * replaces the map, which is what "the log for this lesson" means.
 */
import {
	collection,
	doc,
	getDoc,
	getDocs,
	setDoc,
	updateDoc,
	query,
	where,
	orderBy,
	limit,
	serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { fromStored } from '../pages/EmployeePortal/Warmups/warmupShape';

export const ROUTINES = 'warmupRoutines';

/** The catalog as the portal reads it — one shot, catalog order. */
export const fetchWarmupCatalog = async () => {
	const snap = await getDocs(query(collection(db, 'warmupExercises'), orderBy('sortKey')));
	return snap.docs.map((d) => fromStored(d.id, d.data()));
};

/** The student's saved routine, or null when none has been saved yet. */
export const fetchWarmupRoutine = async (studentId) => {
	if (!studentId) return null;
	const snap = await getDoc(doc(db, ROUTINES, studentId));
	return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Save (replace) the student's routine. */
export const saveWarmupRoutine = async (studentId, { cell, items }, uid = null) => {
	if (!studentId) throw new Error('saveWarmupRoutine needs a studentId');
	await setDoc(doc(db, ROUTINES, studentId), {
		studentId,
		cell,
		items,
		updatedAt: serverTimestamp(),
		updatedBy: uid || null,
	});
};

/**
 * The teacher-only log for this lesson: which exercises were played and how
 * far each climbed. Replaces the whole map — the caller holds the full list.
 */
export const logLessonWarmups = async (lessonId, { exercises, seconds, cell }) => {
	if (!lessonId) return;
	await updateDoc(doc(db, 'lessons', lessonId), {
		warmups: { exercises, seconds, cell: cell || null, updatedAt: serverTimestamp() },
	});
};

/**
 * The most recent OTHER lesson of this student that carries a warmup log.
 * Served by the deployed (studentId ASC, date DESC, time DESC) index — the same
 * query StudentLessonsHistory runs — with a small limit; a student rarely has
 * more than one lesson a week and the log is a recent feature.
 */
export const fetchLastLessonWarmups = async (studentId, excludeLessonId = null, max = 6) => {
	if (!studentId) return null;
	const snap = await getDocs(
		query(
			collection(db, 'lessons'),
			where('studentId', '==', studentId),
			orderBy('date', 'desc'),
			orderBy('time', 'desc'),
			limit(max)
		)
	);
	const hit = snap.docs.find((d) => d.id !== excludeLessonId && d.data().warmups);
	return hit ? { lessonId: hit.id, date: hit.data().date || null, warmups: hit.data().warmups } : null;
};

const WarmupRoutineService = {
	fetchWarmupCatalog,
	fetchWarmupRoutine,
	saveWarmupRoutine,
	logLessonWarmups,
	fetchLastLessonWarmups,
};

export default WarmupRoutineService;
